import { SYN_API_URLS } from '@/constants/api';
import { DEFAULT_DECIMALS, DEFAULT_EMPTY_ACCOUNT, DEFAULT_EMPTY_ACCOUNT_E } from '@/constants/global';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { IGateValueResponse } from '@/types/balance';
import { BlockyResult } from '@/types/worker';
import { bigNumberObjectCheck, debounceRequest } from '@/utils';
import { axiosGet } from '@/utils/axios';
import { fixBalanceNumberDecimalsTo18, getTokenInfo } from '@/utils/token';
import { Context, TokenInfo as Token } from '@derivation-tech/context';
import { NATIVE_TOKEN_ADDRESS } from '@synfutures/sdks-perp';
import { BigNumber } from 'ethers';
import { ITokenBalanceFromApi, ITokenBalanceFromApiResponse } from './types';

export async function getGateBalanceFromChain(
  sdk: Context,
  userAddr: string,
  tokens: Token[],
  block?: number,
): Promise<
  | BlockyResult<
      {
        token: Token;
        balance: BigNumber;
        block: number;
      }[]
    >
  | undefined
> {
  if (!sdk) return;
  if (tokens.length === 0) {
    return;
  }

  tokens = tokens.map((token) =>
    token.address.toLowerCase() === NATIVE_TOKEN_ADDRESS.toLowerCase() ? sdk.wrappedNativeToken : token,
  );

  const quotes = tokens.map((t) => t.address);

  const [balances, blockInfo] = await sdk.perp.contracts.observer.getVaultBalances(userAddr, quotes, {
    blockTag: block,
  });
  if (blockInfo) {
    block = blockInfo[1];
  }

  return {
    block: block || 0,
    data: tokens.map((token, index) => {
      return {
        token: { ...token, address: token.address.toLowerCase() },
        balance: balances[index],
        block: block || 0,
      };
    }),
  };
}

export const fetchUserGateBalance = debounceRequest(
  async (chainId: number, userAddress: string): Promise<IGateValueResponse | null> => {
    const res = await axiosGet({
      url: SYN_API_URLS.PORTFOLIO.USER_GATE_VALUE,
      config: { params: { chainId, userAddress: userAddress } },
    });
    if (res?.data?.data.portfolios) {
      const data = bigNumberObjectCheck(res.data.data.portfolios, [
        'totalInBalance',
        'totalOutBalance',
        'balance',
        'amount',
        'exemption',
        'thresholdBalance',
        'reservedBalance',
        'maxWithdrawable',
      ]);
      if (data?.length) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const portfolios = data.map((d: any) => {
          const info = {
            ...d,

            balance: WrappedBigNumber.from(
              fixBalanceNumberDecimalsTo18(BigNumber.from(d.balance), d?.decimals || DEFAULT_DECIMALS),
            ),
            decimals: Number(d?.decimals || DEFAULT_DECIMALS),
            address: d?.quote?.toLowerCase(),
            pendingBalance: d.pendingBalance
              ? {
                  timestamp: Number(d.pendingBalance.timestamp || 0),
                  amount: WrappedBigNumber.from(
                    fixBalanceNumberDecimalsTo18(
                      BigNumber.from(d.pendingBalance.amount),
                      d?.decimals || DEFAULT_DECIMALS,
                    ),
                  ),
                  exemption: WrappedBigNumber.from(
                    fixBalanceNumberDecimalsTo18(
                      BigNumber.from(d.pendingBalance.exemption),
                      d?.decimals || DEFAULT_DECIMALS,
                    ),
                  ),
                }
              : undefined,
            totalInBalance: WrappedBigNumber.from(
              fixBalanceNumberDecimalsTo18(BigNumber.from(d.totalInBalance), d?.decimals || DEFAULT_DECIMALS),
            ),
            totalOutBalance: WrappedBigNumber.from(
              fixBalanceNumberDecimalsTo18(BigNumber.from(d.totalOutBalance), d?.decimals || DEFAULT_DECIMALS),
            ),
            thresholdBalance: WrappedBigNumber.from(
              fixBalanceNumberDecimalsTo18(BigNumber.from(d.thresholdBalance), d?.decimals || DEFAULT_DECIMALS),
            ),
            reservedBalance: WrappedBigNumber.from(
              fixBalanceNumberDecimalsTo18(BigNumber.from(d.reservedBalance), d?.decimals || DEFAULT_DECIMALS),
            ),
            maxWithdrawable: WrappedBigNumber.from(
              fixBalanceNumberDecimalsTo18(BigNumber.from(d.maxWithdrawable), d?.decimals || DEFAULT_DECIMALS),
            ),
          };
          return getTokenInfo(info, chainId);
        });

        return {
          ...res?.data?.data,
          portfolios,
          pendingDuration: Number(res?.data?.data?.pendingDuration),
        };
      }
    }
    return null;
  },
  500,
);

export const fetchUserTokenBalance = async (
  chainId: number,
  address: string,
  spender?: string,
): Promise<ITokenBalanceFromApiResponse> => {
  const res = await axiosGet({
    url: SYN_API_URLS.PORTFOLIO.USER_ALL_TOKEN_BALANCE,
    config: { params: { chainId, address, ...(spender ? { spender } : {}) } },
  });
  return res.data.data.map((d: ITokenBalanceFromApi) => ({
    ...d,
    id: d.address === DEFAULT_EMPTY_ACCOUNT_E ? DEFAULT_EMPTY_ACCOUNT : d.address,
    address: d.address === DEFAULT_EMPTY_ACCOUNT_E ? DEFAULT_EMPTY_ACCOUNT : d.address,
    current: WrappedBigNumber.from(d.current),
    high24h: WrappedBigNumber.from(d.high_24h),
    low24h: WrappedBigNumber.from(d.low_24h),
    balance: WrappedBigNumber.from(
      fixBalanceNumberDecimalsTo18(BigNumber.from(d.balance), d?.decimals || DEFAULT_DECIMALS),
    ),
    allowance: WrappedBigNumber.from(
      fixBalanceNumberDecimalsTo18(BigNumber.from(d.allowance), d?.decimals || DEFAULT_DECIMALS),
    ),
  }));
};
